import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { authService } from '@/services/auth';
import { apiClient } from '@/services/api';
import { useRoleContext } from '@/contexts/RoleContext';
import { SessionTimeoutModal } from '@/components/SessionTimeoutModal';
import type { User } from '@shared/types';

// Warning shows 5 minutes before token expiry
const WARNING_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

// Synchronously check if tokens exist in localStorage (for initial state)
function hasTokensInStorage(): boolean {
  try {
    const stored = localStorage.getItem('cognitoTokens');
    if (!stored) return false;
    const tokens = JSON.parse(stored);
    return tokens && tokens.expiresAt > Date.now();
  } catch {
    return false;
  }
}

interface AuthContextType {
  user: User | null;
  actualUser: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [actualUser, setActualUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidTokens, setHasValidTokens] = useState(() => hasTokensInStorage());
  const roleContext = useRoleContext();

  // Session timeout state
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [timeoutSecondsLeft, setTimeoutSecondsLeft] = useState(0);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const countdownRef = useRef<ReturnType<typeof setInterval>>();

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = undefined;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = undefined;
    }
  }, []);

  const loadUser = async () => {
    try {
      const isAuth = await authService.isAuthenticated();
      setHasValidTokens(isAuth);

      if (isAuth) {
        const backendUser = await apiClient.getCurrentUser();
        setActualUser(backendUser);
      } else {
        setActualUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      if (!hasValidTokens) {
        setActualUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startSessionTimer = useCallback(() => {
    clearTimers();

    const expiresAt = authService.getTokenExpiryTime();
    if (!expiresAt) return;

    const now = Date.now();
    const timeUntilWarning = expiresAt - now - WARNING_BEFORE_EXPIRY_MS;

    if (timeUntilWarning <= 0) {
      // Already past warning threshold — try a silent refresh first
      authService.refreshSession().then((session) => {
        if (session) {
          // Refresh succeeded, restart timer with new expiry
          startSessionTimer();
        } else {
          // Refresh failed — logout immediately
          authService.logout();
        }
      });
      return;
    }

    warningTimerRef.current = setTimeout(() => {
      setShowTimeoutModal(true);
      const warningSeconds = Math.floor(WARNING_BEFORE_EXPIRY_MS / 1000);
      setTimeoutSecondsLeft(warningSeconds);

      countdownRef.current = setInterval(() => {
        setTimeoutSecondsLeft((prev) => {
          if (prev <= 1) {
            // Time's up — logout
            clearTimers();
            setShowTimeoutModal(false);
            authService.logout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeUntilWarning);
  }, [clearTimers]);

  // Start session timer when authenticated
  useEffect(() => {
    if (hasValidTokens) {
      startSessionTimer();
    } else {
      clearTimers();
      setShowTimeoutModal(false);
    }
    return () => clearTimers();
  }, [hasValidTokens, startSessionTimer, clearTimers]);

  useEffect(() => {
    loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = () => {
    authService.login();
  };

  const logout = () => {
    clearTimers();
    setShowTimeoutModal(false);
    // Don't update React state (setActualUser/setHasValidTokens) before navigating.
    // authService.logout() does window.location.href = '/logout' which reloads the page,
    // resetting all state. Updating state first causes ProtectedRoute to briefly
    // redirect to /login before the navigation completes (flicker).
    authService.logout();
  };

  const refreshUser = async () => {
    await loadUser();
  };

  const refreshAuth = useCallback(async () => {
    setIsLoading(true);
    await loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSession = async (): Promise<boolean> => {
    try {
      const session = await authService.refreshSession();
      return session !== null;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  const handleContinueSession = async () => {
    clearTimers();
    setShowTimeoutModal(false);
    const success = await refreshSession();
    if (success) {
      startSessionTimer();
    } else {
      // Refresh failed — logout
      authService.logout();
    }
  };

  const handleSignOut = () => {
    logout();
  };

  const effectiveUser = roleContext.getEffectiveUser(actualUser);

  const value: AuthContextType = {
    user: effectiveUser,
    actualUser,
    isLoading,
    isAuthenticated: hasValidTokens,
    login,
    logout,
    refreshUser,
    refreshAuth,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutModal
        visible={showTimeoutModal}
        remainingSeconds={timeoutSecondsLeft}
        onContinue={handleContinueSession}
        onSignOut={handleSignOut}
      />
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProviderInner>
      {children}
    </AuthProviderInner>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
